import React, { useContext, useState, useEffect, memo, useCallback } from "react";
import ImageComponent from "../../common/ImageComponent";
import { Language } from "../../../App";
import { _imagesURL } from "../../../constants/API";
import {
  EVENT_DETAILS,
  NEW_COURSE_DETAILS,
  SEE_ALL_POPULAR_COURSES_PAGE,
  SEE_ALL_SUGGESTED_EVENTS,
} from "../../../constants/RoutesUrl";
import { useNavigate } from "react-router-dom";
import { IoIosArrowForward } from "react-icons/io";
import {
  MdOutlineCalendarMonth,
  MdOutlineSchool,
  MdOutlineEventNote,
  MdOutlineChevronRight,
  MdShoppingBag,
} from "react-icons/md";
import "../../agenda/AgendaUpcomingSidebar.css";
import "../../agenda/AgendaFoundation.css";
import defaultImage from "../../../assets/images/default_agenda_1.jpg";

/* ── Shimmer (matching LiveFeeds.jsx / AgendaUpcomingSidebar style) ── */
const SuggestedShimmerRow = ({ titleWidth = "68%", subWidth = "44%", badgeWidth = "30%" }) => (
  <div className="agenda-sidebar-shimmer-row" aria-hidden="true">
    {/* Thumbnail */}
    <div className="agenda-sidebar-shimmer-thumb agenda-shimmer-block" />

    {/* Info Column */}
    <div className="agenda-sidebar-shimmer-info">
      {/* Line 1: Title */}
      <div className="d-flex align-items-center justify-content-between gap-2 w-100">
        <span
          className="agenda-sidebar-shimmer-title agenda-shimmer-block"
          style={{ width: titleWidth }}
        />
        <span
          className="agenda-shimmer-block"
          style={{ width: "36px", height: "18px", borderRadius: "5px", flexShrink: 0 }}
        />
      </div>

      {/* Line 2: Sub-text */}
      <div className="d-flex align-items-center gap-2 w-100">
        <span
          className="agenda-sidebar-shimmer-meta agenda-shimmer-block"
          style={{ width: subWidth }}
        />
      </div>

      {/* Line 3: Badge */}
      <div className="d-flex align-items-center gap-2 w-100">
        <span
          className="agenda-sidebar-shimmer-badge agenda-shimmer-block"
          style={{ width: badgeWidth, height: "18px", borderRadius: "5px" }}
        />
      </div>
    </div>
  </div>
);

const SuggestedShimmer = () => (
  <div className="agenda-sidebar-shimmer-container" role="status" aria-label="Loading">
    <div className="agenda-sidebar-shimmer-group">
      <span
        className="agenda-sidebar-shimmer-group-label agenda-shimmer-block"
        style={{ width: "100px", height: "14px", borderRadius: "4px" }}
      />
      <div className="agenda-timeline-items">
        <SuggestedShimmerRow titleWidth="72%" subWidth="48%" badgeWidth="32%" />
        <SuggestedShimmerRow titleWidth="60%" subWidth="38%" badgeWidth="26%" />
        <SuggestedShimmerRow titleWidth="68%" subWidth="44%" badgeWidth="30%" />
      </div>
    </div>
  </div>
);

export default function SuggestedCoursesEvents({
  type = "courses",
  tabType = "default",
  suggestedEvents,
  joinedEvents,
  popularCourses,
  purchasedCourses,
  viewAllPurchased,
  coursePosition,
  setSelectedCourseType,
  selectedCourseType,
  isLoading,
  loading: loadingProp,
}) {
  const { lang } = useContext(Language);
  const [activeTab, setActiveTab] = useState(1);
  const [internalLoading, setInternalLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const hasData = Boolean(
      (popularCourses && popularCourses.length > 0) ||
      (suggestedEvents && suggestedEvents.length > 0) ||
      (joinedEvents && joinedEvents.length > 0) ||
      (purchasedCourses && purchasedCourses.length > 0)
    );
    if (hasData) setInternalLoading(false);
  }, [popularCourses, suggestedEvents, joinedEvents, purchasedCourses]);

  useEffect(() => {
    const timer = setTimeout(() => setInternalLoading(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  const isDataExplicitlyLoading = isLoading !== undefined ? isLoading : (loadingProp !== undefined ? loadingProp : null);

  const tabsConfig = {
    courses: [
      { id: 1, title: lang?.popular || "Popular", icon: MdOutlineSchool },
      { id: 2, title: lang?.purchased || "Purchased", icon: MdShoppingBag },
    ],
    events: [
      { id: 1, title: lang?.suggested || "Suggested", icon: MdOutlineEventNote },
      { id: 2, title: lang?.joined || "Joined", icon: MdOutlineEventNote },
    ],
    "courses-and-events": [
      { id: 1, title: lang?.courses || "Courses", icon: MdOutlineSchool },
      { id: 2, title: lang?.events || "Events", icon: MdOutlineEventNote },
    ],
  };

  const tabsArray = tabsConfig[tabType] || tabsConfig.courses;

  const hasPurchasedCourses = Boolean(purchasedCourses && purchasedCourses.length > 0);

  // For courses-and-events: tab 1 = courses, tab 2 = events
  const isCourseTab = tabType === "courses" ||
    (tabType === "courses-and-events" && activeTab === 1);

  const getItemsToDisplay = () => {
    if (tabType === "courses") {
      return hasPurchasedCourses ? (purchasedCourses || []) : (popularCourses || []);
    }
    const tabsMap = {
      events: [joinedEvents || [], suggestedEvents || []],
      "courses-and-events": [suggestedEvents || [], popularCourses || []],
    };
    const items = tabsMap[tabType];
    return items ? items[activeTab === 1 ? 1 : 0] : [];
  };

  const rawItems = getItemsToDisplay() || [];
  const itemsToDisplay = rawItems.slice(0, 5);
  const showShimmer = isDataExplicitlyLoading !== null
    ? isDataExplicitlyLoading
    : (itemsToDisplay.length === 0 && internalLoading);

  const handleViewAll = () => {
    if (tabType === "courses") {
      if (hasPurchasedCourses) {
        if (viewAllPurchased) {
          viewAllPurchased(1, "/courses", "courses");
        } else {
          navigate("/courses");
        }
      } else {
        navigate(SEE_ALL_POPULAR_COURSES_PAGE);
      }
      return;
    }

    const viewAllMap = {
      events: {
        1: () => navigate(SEE_ALL_SUGGESTED_EVENTS),
        0: () => navigate("/events/3"),
      },
      "courses-and-events": {
        1: () => navigate(SEE_ALL_POPULAR_COURSES_PAGE),
        0: () => navigate(SEE_ALL_SUGGESTED_EVENTS),
      },
    };
    const tabActions = viewAllMap[tabType];
    if (tabActions) {
      const action = tabActions[activeTab === 1 ? 1 : 0];
      if (action) action();
    }
  };

  const tabColor = isCourseTab
    ? { primary: "#00BADA", light: "#E8FBFF", border: "#BCEEFA" }
    : { primary: "#F59E0B", light: "#FEF3C7", border: "#FDE68A" };

  return (
    <>
      <style>{`
        @keyframes mrShimmerWave {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes sce-fadeUp {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .sce-fade { animation: sce-fadeUp 0.22s cubic-bezier(0.4,0,0.2,1) forwards; }

        .sce-pill-tab {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 6px 10px;
          border-radius: 10px;
          border: none;
          font-size: 12px;
          font-weight: 600;
          font-family: 'Onest', sans-serif;
          cursor: pointer;
          transition: all 0.22s ease;
          outline: none;
          flex: 1 1 0;
          min-width: 0;
          justify-content: center;
        }
        .sce-pill-tab.active {
          color: #FFFFFF !important;
          box-shadow: 0 4px 12px rgba(0,186,218,0.30);
        }
        .sce-pill-tab.inactive {
          background: transparent !important;
          color: #64748B !important;
        }
        .sce-pill-tab.inactive:hover {
          background: #F1F5F9 !important;
          color: #0F172A !important;
        }
        .sce-scroll-container {
          display: flex;
          flex-direction: column;
          overflow: visible;
        }

        .sce-item-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 9px 14px;
          border-radius: 14px;
          cursor: pointer;
          transition: background 0.18s ease, transform 0.18s ease;
          text-decoration: none;
          color: inherit;
        }
        .sce-item-row:hover {
          background: #F0FBFF;
          transform: translateX(2px);
        }
        .sce-item-row:hover .sce-chevron {
          background: #00BADA !important;
          color: #fff !important;
          transform: translateX(2px);
        }
        .sce-chevron {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #F1F5F9;
          color: #94A3B8;
          flex-shrink: 0;
          transition: all 0.18s ease;
        }

        .sce-thumb {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          overflow: hidden;
          flex-shrink: 0;
          border: 1.5px solid #E2E8F0;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
      `}</style>

      <div
        className={`position-relative overflow-hidden bg-white d-xl-block d-none mb-3 ${coursePosition || ""}`}
        style={{
          width: tabType === "courses" ? "100%" : "335px",
          maxWidth: tabType === "courses" ? "100%" : "335px",
          top: coursePosition ? "5rem" : "auto",
          borderRadius: tabType === "courses" ? "16px" : "24px",
          border: "1px solid #EBF1F6",
          background: "linear-gradient(180deg, #FFFFFF 0%, #F8FDFF 100%)",
          boxShadow: "0 2px 8px rgba(11, 19, 43, 0.03)",
          transition: "all 0.25s ease",
          fontFamily: "'Onest', sans-serif",
        }}
      >
        {/* Ambient Glow */}
        <div
          style={{
            position: "absolute", top: "-30px", right: "-30px",
            width: "120px", height: "120px",
            background: tabColor.primary,
            opacity: 0.10, filter: "blur(45px)",
            borderRadius: "50%", pointerEvents: "none", zIndex: 1,
          }}
        />

        {/* ── Header: Icon + Title + Pill Tabs ── */}
        <div
          className="position-relative"
          style={{ padding: "14px 16px 10px", zIndex: 2 }}
        >
          {/* Title row */}
          <div className={`d-flex align-items-center justify-content-between ${tabType === "courses" ? "mb-1" : "mb-3"}`}>
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: "28px", height: "28px",
                  background: tabColor.light,
                  color: tabColor.primary,
                  border: `1px solid ${tabColor.border}`,
                  transition: "all 0.25s ease",
                }}
              >
                {tabType === "courses" ? (
                  hasPurchasedCourses ? <MdShoppingBag size={15} /> : <MdOutlineSchool size={15} />
                ) : isCourseTab ? (
                  <MdOutlineSchool size={15} />
                ) : (
                  <MdOutlineEventNote size={15} />
                )}
              </div>
              <h4 className="fs-15 roboto-medium mb-0" style={{ color: "#0F1A2B", lineHeight: 1.2, fontWeight: 600 }}>
                {tabType === "courses"
                  ? (hasPurchasedCourses
                      ? (lang?.purchased_courses || "Purchased Courses")
                      : (lang?.popular_courses || "Popular Courses"))
                  : isCourseTab
                  ? (lang?.popular_courses || "Courses")
                  : (activeTab === 2
                      ? (lang?.joined_events || "Joined Events")
                      : (lang?.suggested_events || "Suggested Events"))}
              </h4>
            </div>
            {itemsToDisplay?.length > 0 && (
              <span
                className="fs-12 roboto-medium cursor d-inline-flex align-items-center gap-1 hover-blue"
                style={{ color: tabColor.primary, transition: "all 0.2s ease", fontWeight: 600 }}
                onClick={handleViewAll}
              >
                {lang?.view_all || "View all"} <MdOutlineChevronRight size={16} />
              </span>
            )}
          </div>

          {/* Pill toggle tabs (hidden when tabType === 'courses' since it dynamically shows Purchased or Popular) */}
          {tabType !== "courses" && tabsArray.length > 1 && (
            <div
              className="d-flex gap-1 p-1 w-100"
              style={{
                background: "#F1F5F9",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              {tabsArray.map((tab) => {
                const isActive = activeTab === tab.id;
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`sce-pill-tab ${isActive ? "active" : "inactive"}`}
                    onClick={() => {
                      setActiveTab(tab.id);
                      if (setSelectedCourseType) setSelectedCourseType(tab.id);
                    }}
                    style={{
                      background: isActive
                        ? isCourseTab
                          ? "linear-gradient(135deg, #00BADA 0%, #0099B8 100%)"
                          : `linear-gradient(135deg, ${tabColor.primary} 0%, #D97706 100%)`
                        : "transparent",
                    }}
                    aria-label={tab.title}
                  >
                    <TabIcon size={13} style={{ flexShrink: 0 }} />
                    <span className="text-truncate" style={{ minWidth: 0 }}>
                      {tab.title}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={{ height: "1px", background: "#F1F5F9", margin: "0 16px" }} />

        {/* ── Content Area ── */}
        <div className="py-2 px-2 position-relative" style={{ zIndex: 2 }}>
          {showShimmer ? (
            <SuggestedShimmer />
          ) : itemsToDisplay?.length > 0 ? (
            <div key={activeTab} className="sce-fade sce-scroll-container">
              {itemsToDisplay.map((item, index) => (
                <React.Fragment key={item.id || index}>
                  {isCourseTab
                    ? <CourseCard course={item} accentColor={tabColor.primary} />
                    : <EventCard event={item} accentColor={tabColor.primary} />}
                  {index !== itemsToDisplay.length - 1 && (
                    <div style={{ height: "1px", background: "#F1F5F9", margin: "2px 12px" }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div
              key={activeTab}
              className="text-center py-4 d-flex flex-column align-items-center justify-content-center sce-fade"
              style={{ minHeight: "110px" }}
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                style={{
                  width: "44px", height: "44px",
                  background: tabColor.light,
                  color: tabColor.primary,
                  border: `1px solid ${tabColor.border}`,
                }}
              >
                {isCourseTab ? <MdOutlineSchool size={22} /> : <MdOutlineEventNote size={22} />}
              </div>
              <p className="fs-13 roboto-medium mb-0" style={{ color: "#94A3B8" }}>
                {lang?.no_content_available_yet || "No content available yet"}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ── Course Card ────────────────────────── */
export const CourseCard = ({ course, accentColor = "#00BADA" }) => {
  const navigate = useNavigate();
  const { lang } = useContext(Language);

  const isFree = course?.price === "0" || course?.price === 0 || !course?.price;
  const priceDisplay = isFree ? (lang?.free || "Free") : `€${course?.price}`;
  const priceColor = isFree
    ? { text: "#059669", bg: "#ECFDF5", border: "#A7F3D0" }
    : { text: accentColor, bg: "#E8FBFF", border: "#BCEEFA" };

  return (
    <div
      className="sce-item-row"
      onClick={() => {
        if (course?.id) {
          sessionStorage.setItem("CourseId", course.id);
          navigate(NEW_COURSE_DETAILS + course.id);
        }
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" && course?.id) {
          sessionStorage.setItem("CourseId", course.id);
          navigate(NEW_COURSE_DETAILS + course.id);
        }
      }}
    >
      {/* Thumbnail */}
      <div className="sce-thumb">
        <ImageComponent
          src={_imagesURL + course?.cover}
          compClassName="w-100 h-100 object-cover"
          img_height="100%"
          img_width="100%"
          loader_height="100%"
          loader_width="100%"
          roundShimmerSize={40}
        />
      </div>

      {/* Info */}
      <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
        <p
          className="fs-14 roboto-medium mb-0 text-truncate hover-blue"
          style={{ color: "#0F1A2B", lineHeight: 1.3, fontWeight: 600 }}
          title={course?.title}
        >
          {course?.title}
        </p>
        <span className="fs-12 roboto-regular text-truncate mt-0.5" style={{ color: "#5F6673" }}>
          {course?.user?.name || lang?.doctor || "Doctor"}
        </span>
      </div>

      {/* Price + Chevron */}
      <div className="d-flex align-items-center gap-2 flex-shrink-0">
        <span
          className="badge rounded-pill"
          style={{
            fontSize: "10.5px",
            fontWeight: 700,
            backgroundColor: priceColor.bg,
            color: priceColor.text,
            border: `1px solid ${priceColor.border}`,
            padding: "3px 9px",
            lineHeight: 1.3,
          }}
        >
          {priceDisplay}
        </span>
        <div className="sce-chevron">
          <IoIosArrowForward size={12} />
        </div>
      </div>
    </div>
  );
};

/* ── Event Card ─────────────────────────── */
const truncateText = (text, maxLength) =>
  text?.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

export const EventCard = memo(({ event = {}, accentColor = "#F59E0B" }) => {
  const { id, title, description, cover, start, end } = event || {};
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (id) navigate(EVENT_DETAILS + id);
  }, [navigate, id]);

  const hasDates = Boolean(start || end);

  return (
    <div
      className="sce-item-row"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
    >
      {/* Thumbnail */}
      <div className="sce-thumb">
        <ImageComponent
          src={cover ? _imagesURL + cover : defaultImage}
          compClassName="w-100 h-100 object-cover"
          img_height="100%"
          img_width="100%"
          loader_height="100%"
          loader_width="100%"
          roundShimmerSize={40}
        />
      </div>

      {/* Info */}
      <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
        <p
          className="fs-14 roboto-medium mb-0 text-truncate hover-blue"
          style={{ color: "#0F1A2B", lineHeight: 1.3, fontWeight: 600 }}
          title={title}
        >
          {title}
        </p>
        {hasDates ? (
          <span
            className="d-inline-flex align-items-center gap-1 mt-0.5"
            style={{ color: "#F59E0B", fontSize: "11px", fontWeight: 600 }}
          >
            <MdOutlineCalendarMonth size={12} style={{ flexShrink: 0 }} />
            <span className="text-truncate">{start && end ? `${start}` : start || end}</span>
          </span>
        ) : (
          <span className="fs-12 roboto-regular text-truncate mt-0.5" style={{ color: "#5F6673" }}>
            {description ? truncateText(description, 22) : "Medical Event"}
          </span>
        )}
      </div>

      {/* Chevron */}
      <div className="sce-chevron flex-shrink-0">
        <IoIosArrowForward size={12} />
      </div>
    </div>
  );
});